import { Popover } from '@headlessui/react';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import Button from 'layout/atoms/Button';
import ContentBox from 'layout/atoms/ContentBox';
import Grid from 'layout/atoms/Grid';
import Portal from 'layout/atoms/Portal';
import styles from './dropdown.module.scss';

import { ReactComponent as DotsHorizontalIcon } from 'assets/icons/dots-horizontal.svg';
import { ReactComponent as DotsVerticalIcon } from 'assets/icons/dots-vertical.svg';

type DropdownProps = {
    open?: boolean;
    position?:
        | 'bottomLeft'
        | 'bottomCenter'
        | 'bottomRight'
        | 'topLeft'
        | 'topCenter'
        | 'topRight';
    horizontal?: boolean;
    trigger?: React.ReactNode | React.ReactNode[];
    children: React.ReactNode | React.ReactNode[];
};

const Dropdown: React.FC<DropdownProps> = ({
    open = false,
    position,
    trigger,
    children,
    horizontal,
}: DropdownProps): JSX.Element => {
    const [active, setActive] = useState<boolean>(open);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [panelStyles, setPanelStyles] = useState<
        React.CSSProperties | undefined
    >(undefined);

    const calculatePosition = useCallback(() => {
        if (!triggerRef.current) return;

        const rect = triggerRef.current.getBoundingClientRect();
        const styles: React.CSSProperties = {
            position: 'absolute',
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
        };

        switch (position) {
            case 'bottomCenter':
                styles.left = rect.left + window.scrollX + rect.width / 2;
                styles.transform = 'translateX(-50%)';
                break;
            case 'bottomRight':
                styles.left = rect.right + window.scrollX;
                styles.transform = 'translateX(-100%)';
                break;
            case 'topLeft':
                styles.top = rect.top + window.scrollY;
                styles.left = rect.left + window.scrollX;
                styles.transform = 'translateY(-100%)';
                break;
            case 'topCenter':
                styles.top = rect.top + window.scrollY;
                styles.left = rect.left + window.scrollX + rect.width / 2;
                styles.transform = 'translate(-50%, -100%)';
                break;
            case 'topRight':
                styles.top = rect.top + window.scrollY;
                styles.left = rect.right + window.scrollX;
                styles.transform = 'translate(-100%, -100%)';
                break;
            default:
                break;
        }

        setPanelStyles(styles);
    }, [position]);

    useEffect(() => {
        window.addEventListener('resize', calculatePosition);
        return () => window.removeEventListener('resize', calculatePosition);
    }, [calculatePosition]);

    useEffect(() => {
        const observer = new MutationObserver(() => calculatePosition());
        if (triggerRef.current) {
            observer.observe(document.body, { childList: true, subtree: true });
        }
        return () => observer.disconnect();
    }, [calculatePosition]);

    return (
        <Popover className={`${styles.wrapper}`}>
            <Popover.Button
                className={clsx(styles.button, {
                    [styles.buttonLight]: !trigger,
                })}
                ref={triggerRef}
                onClick={() => {
                    setActive(true);
                    calculatePosition();
                }}
                as="div"
            >
                {trigger ? (
                    trigger
                ) : horizontal ? (
                    <Button size="m" theme="blank" shape="square">
                        <DotsHorizontalIcon />
                    </Button>
                ) : (
                    <Button size="m" theme="blank" shape="square">
                        <DotsVerticalIcon />
                    </Button>
                )}
            </Popover.Button>
            {(open || active) && (
                <Portal className={styles.portal}>
                    <Popover.Panel
                        className={`${styles.box} ${
                            position ? styles[position] : styles.bottomLeft
                        }`}
                        style={panelStyles}
                    >
                        {({ close }) => (
                            <div
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                    const target = e.target as HTMLElement;
                                    if (target.closest('[data-keep-open]')) {
                                        return;
                                    }
                                    close();
                                    setActive(false);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Esc' || e.key === ' ') {
                                        close();
                                        setActive(false);
                                    }
                                }}
                            >
                                <ContentBox padding="s">
                                    <Grid columns="1">{children}</Grid>
                                </ContentBox>
                            </div>
                        )}
                    </Popover.Panel>
                </Portal>
            )}
        </Popover>
    );
};

export default Dropdown;
